// =============================================================================
// OpsCalendar — Day / Week / Month planner with drag-to-reschedule
// =============================================================================
// Generic over the row type so both the Manufacturing and Delivery pages reuse
// it. Items are dragged between days to reschedule (HTML5 native DnD, no lib)
// and clicked to open a reschedule modal. Capacity badges are a visual aid only
// — the server validates the actual move and rejects (→ override prompt).
//
// All date math is UTC (see opsShared) so cells line up with the backend, which
// stores delivery/production dates as UTC days.

import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Card } from '../../components/Card';
import { cn } from '../../lib/cn';
import {
  addDaysUTC,
  isoDayUTC,
  monthGridUTC,
  parseIsoUTC,
  todayIsoUTC,
  weekDaysUTC,
  WEEKDAY_LABELS,
} from './opsShared';

export type CalendarView = 'day' | 'week' | 'month';

export interface OpsCalendarProps<T> {
  rows: T[];
  getDay: (r: T) => string | null;
  getId: (r: T) => string;
  /** Only rows that return true can be dragged to a new day. */
  canMove: (r: T) => boolean;
  renderChip: (r: T, ctx: { view: CalendarView }) => ReactNode;
  /** Fired when a row is dropped on a new day. */
  onMove: (row: T, targetIso: string) => void;
  /** Fired when a chip (not one of its action buttons) is clicked. */
  onClickItem?: (row: T) => void;
  /** Optional per-day capacity overlay badge. */
  capacityFor?: (iso: string) => { used: number; cap: number } | null;
  /** Optional action rendered in the Day-view header (e.g. a day-sheet PDF button). */
  dayHeaderAction?: (iso: string) => ReactNode;
  emptyLabel?: string;
}

const fmtCell = (iso: string): string =>
  parseIsoUTC(iso).toLocaleDateString('en-MU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

export function OpsCalendar<T>(props: OpsCalendarProps<T>): JSX.Element {
  const { rows, getDay } = props;
  const [view, setView] = useState<CalendarView>('week');
  const [anchorIso, setAnchorIso] = useState<string>(() => todayIsoUTC());
  const anchor = useMemo(() => parseIsoUTC(anchorIso), [anchorIso]);
  const todayIso = todayIsoUTC();

  const dragRow = useRef<T | null>(null);
  const [dragOverIso, setDragOverIso] = useState<string | null>(null);

  const byDay = useMemo(() => {
    const map = new Map<string, T[]>();
    for (const r of rows) {
      const d = getDay(r);
      if (!d) continue;
      const list = map.get(d) ?? [];
      list.push(r);
      map.set(d, list);
    }
    return map;
  }, [rows, getDay]);

  const unscheduled = useMemo(() => rows.filter((r) => !getDay(r)), [rows, getDay]);

  const step = (dir: -1 | 1): void => {
    if (view === 'day') setAnchorIso(isoDayUTC(addDaysUTC(anchor, dir)));
    else if (view === 'week') setAnchorIso(isoDayUTC(addDaysUTC(anchor, dir * 7)));
    else
      setAnchorIso(
        isoDayUTC(new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth() + dir, 1))),
      );
  };

  const periodLabel =
    view === 'day'
      ? parseIsoUTC(anchorIso).toLocaleDateString('en-MU', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          timeZone: 'UTC',
        })
      : view === 'week'
        ? (() => {
            const w = weekDaysUTC(anchor);
            return `${fmtCell(w[0]!.iso)} – ${fmtCell(w[6]!.iso)}`;
          })()
        : anchor.toLocaleDateString('en-MU', { month: 'long', year: 'numeric', timeZone: 'UTC' });

  const handleDrop = (iso: string, isPast: boolean): void => {
    const r = dragRow.current;
    dragRow.current = null;
    setDragOverIso(null);
    if (!r || isPast) return;
    if (props.getDay(r) === iso) return; // dropped back on its own day — no-op
    props.onMove(r, iso);
  };

  // ── chip ───────────────────────────────────────────────────────────────────
  const Chip = ({ row, view: v }: { row: T; view: CalendarView }): JSX.Element => {
    const movable = props.canMove(row);
    return (
      <div
        draggable={movable}
        onDragStart={(e) => {
          if (!movable) return;
          dragRow.current = row;
          e.dataTransfer.effectAllowed = 'move';
        }}
        onDragEnd={() => {
          dragRow.current = null;
          setDragOverIso(null);
        }}
        onClick={() => props.onClickItem?.(row)}
        className={cn(
          'rounded-md border border-rule-2 bg-surface px-2.5 py-2 text-xs shadow-sm overflow-hidden',
          movable && 'cursor-grab active:cursor-grabbing',
          props.onClickItem && 'hover:border-accent/50 transition',
        )}
        title={movable ? 'Drag to another day, or click to reschedule' : undefined}
      >
        {props.renderChip(row, { view: v })}
      </div>
    );
  };

  // ── droppable day cell (week / month) ───────────────────────────────────────
  const DayCell = ({
    iso,
    inMonth,
    tall,
  }: {
    iso: string;
    inMonth: boolean;
    tall?: boolean;
  }): JSX.Element => {
    const isPast = iso < todayIso;
    const isToday = iso === todayIso;
    const isSun = parseIsoUTC(iso).getUTCDay() === 0;
    const cap = props.capacityFor?.(iso) ?? null;
    const rows = byDay.get(iso) ?? [];
    const dropping = dragOverIso === iso;

    return (
      <div
        onDragOver={(e) => {
          if (dragRow.current && !isPast) {
            e.preventDefault();
            if (dragOverIso !== iso) setDragOverIso(iso);
          }
        }}
        onDragLeave={() => setDragOverIso((cur) => (cur === iso ? null : cur))}
        onDrop={() => handleDrop(iso, isPast)}
        className={cn(
          'flex flex-col gap-1 p-1.5 border-rule-2',
          tall ? 'min-h-[120px]' : 'min-h-[92px]',
          !inMonth && 'bg-paper-2/30',
          isSun && 'bg-paper-2/50',
          isPast && 'opacity-50',
          dropping && 'ring-2 ring-inset ring-accent bg-accent-soft/40',
        )}
      >
        <div className="flex items-center justify-between">
          <span
            className={cn(
              'text-xs kc-serif',
              isToday ? 'text-accent font-semibold' : inMonth ? 'text-ink' : 'text-ink-3/50',
            )}
          >
            {parseIsoUTC(iso).getUTCDate()}
            {isToday && (
              <span className="ml-1 text-[8px] uppercase tracking-wider text-accent">today</span>
            )}
          </span>
          {cap && (
            <span
              className={cn(
                'text-[10px] kc-mono',
                cap.used >= cap.cap ? 'text-negative font-medium' : 'text-ink-3',
              )}
            >
              {cap.used}/{cap.cap}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-1">
          {rows.map((r) => (
            <Chip key={props.getId(r)} row={r} view={view} />
          ))}
        </div>
      </div>
    );
  };

  // ── views ───────────────────────────────────────────────────────────────────
  const renderMonth = (): JSX.Element => {
    const cells = monthGridUTC(anchor);
    return (
      <div>
        <div className="grid grid-cols-7 border-b border-rule-2 bg-paper-2/40">
          {WEEKDAY_LABELS.map((w) => (
            <div
              key={w}
              className="text-[10px] uppercase tracking-[0.18em] text-ink-3 text-center py-1.5 font-medium"
            >
              {w}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 [&>*]:border-r [&>*]:border-b last:[&>*]:border-r-0">
          {cells.map((c) => (
            <DayCell key={c.iso} iso={c.iso} inMonth={c.inMonth} />
          ))}
        </div>
      </div>
    );
  };

  const renderWeek = (): JSX.Element => {
    const days = weekDaysUTC(anchor);
    return (
      <div className="grid grid-cols-7 [&>*]:border-r [&>*]:border-rule-2 last:[&>*]:border-r-0">
        {days.map((d) => (
          <div key={d.iso} className="flex flex-col">
            <div className="text-[11px] text-center py-1.5 border-b border-rule-2 bg-paper-2/40 text-ink-2 font-medium">
              {fmtCell(d.iso)}
            </div>
            <DayCell iso={d.iso} inMonth tall />
          </div>
        ))}
      </div>
    );
  };

  const renderDay = (): JSX.Element => {
    const isPast = anchorIso < todayIso;
    const rows = byDay.get(anchorIso) ?? [];
    const cap = props.capacityFor?.(anchorIso) ?? null;
    return (
      <div
        onDragOver={(e) => {
          if (dragRow.current && !isPast) {
            e.preventDefault();
            if (dragOverIso !== anchorIso) setDragOverIso(anchorIso);
          }
        }}
        onDragLeave={() => setDragOverIso((cur) => (cur === anchorIso ? null : cur))}
        onDrop={() => handleDrop(anchorIso, isPast)}
        className={cn(
          'p-4 min-h-[200px]',
          dragOverIso === anchorIso && 'ring-2 ring-inset ring-accent bg-accent-soft/30',
        )}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm text-ink-2">
            {rows.length} item{rows.length === 1 ? '' : 's'}
            {cap && (
              <span className="ml-2 text-ink-3 kc-mono text-xs">
                · {cap.used}/{cap.cap}
              </span>
            )}
          </div>
          {props.dayHeaderAction?.(anchorIso)}
        </div>
        {rows.length === 0 ? (
          <div className="text-sm text-ink-3 italic py-6 text-center">
            {props.emptyLabel ?? 'Nothing scheduled for this day.'}
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <Chip key={props.getId(r)} row={r} view={view} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const viewBtn = (v: CalendarView, label: string): JSX.Element => (
    <button
      type="button"
      onClick={() => setView(v)}
      className={cn(
        'px-3 py-1.5 rounded text-sm transition',
        view === v ? 'bg-accent-soft text-accent font-medium' : 'text-ink-2 hover:bg-paper-2',
      )}
    >
      {label}
    </button>
  );

  return (
    <Card bodyClassName="p-0">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-b border-rule-2">
        <div className="inline-flex items-center gap-0.5 rounded-md border border-rule bg-surface p-0.5">
          {viewBtn('day', 'Day')}
          {viewBtn('week', 'Week')}
          {viewBtn('month', 'Month')}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => step(-1)}
            className="w-8 h-8 inline-flex items-center justify-center rounded-md border border-rule bg-surface text-ink-2 hover:bg-paper-2 hover:text-ink hover:border-rule-2 transition"
            aria-label="Previous"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => setAnchorIso(todayIsoUTC())}
            className="text-xs font-medium px-3 h-8 rounded-md border border-rule bg-surface text-ink-2 hover:bg-paper-2 hover:text-ink hover:border-rule-2 transition"
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => step(1)}
            className="w-8 h-8 inline-flex items-center justify-center rounded-md border border-rule bg-surface text-ink-2 hover:bg-paper-2 hover:text-ink hover:border-rule-2 transition"
            aria-label="Next"
          >
            ›
          </button>
          <div className="text-sm font-medium text-ink min-w-[12rem] text-right">{periodLabel}</div>
        </div>
      </div>

      {view === 'month' ? renderMonth() : view === 'week' ? renderWeek() : renderDay()}

      {/* Unscheduled bucket — drag onto a day to schedule */}
      {unscheduled.length > 0 && (
        <div className="border-t border-rule-2 px-4 py-3">
          <div className="text-[11px] uppercase tracking-wider text-ink-3 mb-2">
            Unscheduled · drag onto a day
          </div>
          <div className="flex flex-wrap gap-2">
            {unscheduled.map((r) => (
              <div key={props.getId(r)} className="max-w-xs">
                <Chip row={r} view={view} />
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
